import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Waypoint } from "react-waypoint";
import { formatUmbracoMedia } from "../../util/UmbracoMediaHelper";

/*
The image component creates one of these two images:

- And image as is, with its natural height and size.
- An aspect ratio placeholder, that mimics the original aspect ratio of the image

Properties:
- Responsive images (5 sizes: xs, sm, md, lg, xl)
- Alt text
- Caption text
- Width (Natural or aspect ratio)
- Height (Natural or aspect ratio)
- Aspect ratio (If selected, it creates an aspect ratio placeholder)

*/

//Future improvements
//Loading only one source, and using browser size compression to load only the neccessary size
//- LQIP

/*
//Usage:
<Image
    src="https://via.placeholder.com/320x320?text=XSmall"
    srcSm="https://via.placeholder.com/768x300?text=Small"
    srcMd="https://via.placeholder.com/1024x300?text=Medium"
    srcLg="https://via.placeholder.com/1280x300?text=Large"
    srcXl="https://via.placeholder.com/1920x800?text=XLarge"
    alt="Alt text here"
    caption="Caption text comes here"
    width={1}
    height={1}
    aspectRatio />
*/

interface ImageProps
{
    alt?: string;
    src: string;
    srcSm?: string;
    srcMd?: string;
    srcLg?: string;
    srcXl?: string;
    caption?: string;
    width: number;
    height: number;
    aspectRatio?: boolean;
}

interface ImageSizeProps
{
    ratio: string;
}
const FadeIn = keyframes`
     0% { opacity: 0; }
     100% { opacity: 1; }
`;

const FigureWrap = styled.figure`
    margin: 0;
    padding: 0;
    animation: ${FadeIn} .5s forwards;
`;

const FigurePlaceholder = styled.div<ImageSizeProps>`
    height: 0;
    overflow: hidden;
    padding-top: ${props => props.ratio}%;
    position: relative;
`;

const FigurePlaceholderImage = styled.img`
    display: block;
    max-width: 100%;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    object-fit: cover;
`;

const FigureImage = styled.img`
    display: inline-block;
    max-width: 100%;
`;

const FigureCaption = styled.figcaption`
    display: block;
    padding-top: .5rem;
    padding-bottom: .5rem;
    font-size: .85rem;
    opacity: .5;
    font-weight: 500;
`;

const Image: React.FunctionComponent<ImageProps> = (props) =>
{

    //In View state
    const [inView, setInView] = useState(false);

    //Set image size (not used at the time)
    const figurePlaceholder = React.createRef<HTMLDivElement>();

    //Aspect Ratio padding
    const ratio = (props.height / props.width) * 100;

    //srcset
    let srcSetImages = props.src + " 300w";

    if (props.srcSm)
    {
        srcSetImages += ", " + props.srcSm + " 768w";
    }
    if (props.srcMd)
    {
        srcSetImages += ", " + props.srcMd + " 1024w";
    }
    if (props.srcLg)
    {
        srcSetImages += ", " + props.srcLg + " 1280w";
    }
    if (props.srcXl)
    {
        srcSetImages += ", " + props.srcXl + " 1800w";
    }

    const handleOnEnter = () =>
    {
        setInView(true);
    };

    const { src, alt, caption, aspectRatio } = props;

    return (
        <Waypoint onEnter={handleOnEnter} bottomOffset={-200}>
            <FigureWrap>
                {
                    aspectRatio ?
                        <FigurePlaceholder ref={figurePlaceholder} ratio={ratio.toFixed(2)}>
                            {inView ? <FigurePlaceholderImage alt={alt} src={formatUmbracoMedia(src)} srcSet={srcSetImages} /> : null}
                        </FigurePlaceholder> :
                        inView ? <FigureImage alt={alt} src={formatUmbracoMedia(src)} srcSet={srcSetImages} /> : null
                }

                {caption ? <FigureCaption>{caption}</FigureCaption> : null}
            </FigureWrap>
        </Waypoint>
    );
};

export default Image;